// Step Constants for Partner Dashboard
// This file provides constants for step ID and step name matching

// Step IDs mapping
export const STEP_IDS = {
  NDA: 1,
  PARTNERSHIP: 2,
  SHARIYAH: 3,
  LEGAL: 4,
  TECH_SETUP: 5,
  TRAINING: 6,
  LAUNCH: 7
};

// Step Names mapping
export const STEP_NAMES = {
  1: "NDA",
  2: "Partnership Agreement", 
  3: "Shariah Certification",
  4: "Legal Compliance",
  5: "Tech Setup",
  6: "Training & Resources",
  7: "Launch & Community Engagement"
};

// Step Keys mapping (for API data structure)
export const STEP_KEYS = {
  1: 'nda',
  2: 'partnership',
  3: 'shariyah',
  4: 'legal',
  5: 'techSetup',
  6: 'training',
  7: 'launch'
};

/**
 * Step key to API step ID mapping.
 * Used by PartnerAdmin and onboarding API (PUT /partner/onboarding/step/:stepId).
 * Backend expects these numeric step IDs (1-9).
 */
export const STEP_KEY_TO_API_ID = {
  nda: 1,
  meeting: 2,
  partnership: 3,
  productDevelopment: 4,
  shariyah: 5,
  legal: 6,
  techSetup: 7,
  launch: 8,
  businessDocumentation: 9
};

// Step Descriptions mapping
export const STEP_DESCRIPTIONS = {
  1: "Non-Disclosure Agreement",
  2: "Legal partnership documentation 222",
  3: "Ethical finance certification review",
  4: "Regulatory compliance verification",
  5: "Halal finance technology infrastructure",
  6: "Partner training and resource access",
  7: "Community outreach and service launch"
};

// Step Icons mapping (Lucide React icons)
export const STEP_ICONS = {
  1: 'FileText',
  2: 'FileText', 
  3: 'Shield',
  4: 'Scale',
  5: 'Settings',
  6: 'GraduationCap',
  7: 'Users'
};

// Step Prerequisites mapping
export const STEP_PREREQUISITES = {
  1: [], // NDA has no prerequisites
  2: [1], // Partnership requires NDA
  3: [2], // Shariyah requires Partnership
  4: [3], // Legal requires Shariyah
  5: [3], // Tech Setup requires Shariyah
  6: [5], // Training requires Tech Setup
  7: [6]  // Launch requires Training
};

// Step Status mapping
export const STEP_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress', 
  COMPLETED: 'completed',
  DISABLED: 'disabled'
};

// Step Approval mapping
export const STEP_APPROVED_BY = {
  1: 'Partner Agreement',
  2: 'Partner Agreement',
  3: 'Shariah Advisor',
  4: 'Legal Review Team',
  5: 'Tech Implementation Team',
  6: 'Training Team',
  7: 'Launch Team'
};

// Helper functions for step operations
export const StepHelpers = {
  // Get step name by ID
  getStepName: (stepId) => {
    return STEP_NAMES[stepId] || 'Unknown Step';
  },

  // Get step ID by name
  getStepId: (stepName) => {
    const entry = Object.entries(STEP_NAMES).find(([id, name]) => name === stepName);
    return entry ? parseInt(entry[0]) : null;
  },

  // Get step key by ID
  getStepKey: (stepId) => {
    return STEP_KEYS[stepId] || '';
  },

  /** Get API step ID by step key (for onboarding API calls). */
  getApiStepId: (stepKey) => {
    return STEP_KEY_TO_API_ID[stepKey] ?? null;
  },

  // Get step description by ID
  getStepDescription: (stepId) => {
    return STEP_DESCRIPTIONS[stepId] || '';
  },

  // Get step icon by ID
  getStepIcon: (stepId) => {
    return STEP_ICONS[stepId] || 'FileText';
  },

  // Get step prerequisites by ID
  getStepPrerequisites: (stepId) => {
    return STEP_PREREQUISITES[stepId] || [];
  },

  // Get step approval by ID
  getStepApproval: (stepId) => {
    return STEP_APPROVED_BY[stepId] || 'Unknown';
  },

  // Check if step has prerequisites
  hasPrerequisites: (stepId) => {
    return STEP_PREREQUISITES[stepId] && STEP_PREREQUISITES[stepId].length > 0;
  },

  // Get all step IDs
  getAllStepIds: () => {
    return Object.values(STEP_IDS);
  },

  // Get all step names
  getAllStepNames: () => {
    return Object.values(STEP_NAMES);
  },

  // Get step info object
  getStepInfo: (stepId) => {
    return {
      id: stepId,
      name: STEP_NAMES[stepId],
      key: STEP_KEYS[stepId],
      description: STEP_DESCRIPTIONS[stepId],
      icon: STEP_ICONS[stepId],
      prerequisites: STEP_PREREQUISITES[stepId],
      approvedBy: STEP_APPROVED_BY[stepId]
    };
  }
};

// Export all constants as default
export default {
  STEP_IDS,
  STEP_NAMES,
  STEP_KEYS,
  STEP_KEY_TO_API_ID,
  STEP_DESCRIPTIONS,
  STEP_ICONS,
  STEP_PREREQUISITES,
  STEP_STATUS,
  STEP_APPROVED_BY,
  StepHelpers
};
